var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { RelayEnvironmentProvider } from "react-relay";
import { Environment, Network, Observable, RecordSource, Store, } from "relay-runtime";
import { initEnvironmentDetails } from "../Shared/components/LoggingContext/LoggingProvider";
import Loading from "../Shared/components/loading";
import { LogLevel } from "../Shared/hooks/useLogging/Logging-types";
import { useLogging } from "../Shared/hooks/useLogging/useLogging";
import { fetchEnvironmentDetails, getHostFromURL } from "./relayEnvironment-utils";
// Function to create a Relay environment using the provided GraphQL endpoint and host.
const createRelayEnvironment = (graphqlEndpoint, host, dataDogLog) => new Environment({
    network: Network.create(fetchFunction(graphqlEndpoint, host, dataDogLog)),
    store: new Store(new RecordSource(), {
        // The queryCacheExpirationTime option in Relay applies globally to
        // all queries stored in memory via that Relay store instance.
        // https://relay.dev/docs/v16.0.0/guided-tour/reusing-cached-data/staleness-of-data/#query-cache-expiration-time
        queryCacheExpirationTime: 60 * 60 * 1000, // Cache expiration time set to 1 hour
    }),
});
// Fetch function to handle GraphQL requests.
const fetchFunction = (graphqlEndpoint, host, dataDogLog) => (operation, variables) => Observable.create((dataSink) => {
    fetch(graphqlEndpoint, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            RequestHost: host,
        },
        body: JSON.stringify({
            query: operation.text,
            variables,
        }),
    })
        .then((response) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        const json = yield response.json();
        if (response.ok) {
            dataSink.next(json);
            dataSink.complete();
        }
        else {
            const errorMessage = ((_b = (_a = json.errors) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.message) || "Unknown error";
            dataDogLog(errorMessage, LogLevel.ERROR, response);
            dataSink.error(new Error(errorMessage));
        }
    }))
        .catch((error) => {
        const errorMessage = error.message || "Network error";
        dataDogLog(errorMessage, LogLevel.ERROR, undefined, error);
        dataSink.error(new Error(errorMessage));
    });
});
// Main RelayEnvironment component
export const RelayEnvironment = ({ children }) => {
    var _a, _b, _c;
    const { datadogLog } = useLogging();
    // Initialize state for the environment.
    const [state, setState] = useState({
        loading: true,
        data: initEnvironmentDetails,
        relayEnvironment: undefined,
    });
    // Fetch the environment details when the component mounts.
    useEffect(() => {
        let mounted = true;
        if (mounted)
            fetchEnvironmentDetails(setState);
        return () => {
            mounted = false;
        };
    }, []);
    // Attempt to create the relay environment when the data is fetched.
    useEffect(() => {
        var _a;
        let mounted = true;
        if (mounted && ((_a = state.data) === null || _a === void 0 ? void 0 : _a.success) && !!state.data.graphQLEndpoint) {
            // Get the host from the environment details.
            const graphQLHost = getHostFromURL(state.data.environment);
            // Create the relay environment if the host is found.
            const relayEnvironment = graphQLHost
                ? createRelayEnvironment(state.data.graphQLEndpoint, graphQLHost, datadogLog)
                : undefined; // If the host is not found, return undefined and force an error state.
            setState((prev) => (Object.assign(Object.assign({}, prev), { relayEnvironment })));
        }
        return () => {
            mounted = false;
        };
    }, [state.data]);
    // If loading, show loading component.
    if (state.loading) {
        return _jsx(Loading, {});
    }
    // ! CREATE AN ERROR COMPONENT
    // If there is an error, show error component.
    if (!((_a = state.data) === null || _a === void 0 ? void 0 : _a.success) || !state.relayEnvironment) {
        return _jsx("div", { children: (_c = (_b = state.data) === null || _b === void 0 ? void 0 : _b.errorMessage) !== null && _c !== void 0 ? _c : "An error has occurred." });
    }
    // If there is no error and the environment is created, return the RelayEnvironmentProvider.
    return _jsx(RelayEnvironmentProvider, Object.assign({ environment: state.relayEnvironment }, { children: children }));
};
